import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, auth, functions } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, setDoc, updateDoc, where, getDocs, documentId, orderBy, limit } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import TroubleshootBox from '../components/TroubleshootBox';
import { ipfsGateway, FIREBASE_PROJECT_ID, API_BASE_URL, BASE_MAX_STAMINA, TOKEN_CONTRACT, TOKENS, tokenContracts, tokenDecimals, buildingConfigClient, ATOMIC_API_ENDPOINTS, itemTemplates, EMBLEM_NAMES, MASTER_ACCOUNT_WAX_ID, ADMIN_ACCOUNTS, STAKING_TIERS, VAULT_TIERS, VAULT_TOKEN_INFO, VAULT_TOKEN_VALUE, getUpgradeCostClient, Client, calculateDiminishedBoost, calculateMaxHp, calculateXpForNextLevel } from '../constants/gameConfig';
import characterImage from '../assets/firecat.png';
import placeholderImage from '../assets/equip.png';

// Sub-component for the Advanced custom field transfer inside the Point Manager
function CustomFieldTransfer({ callable, setPtLoading, setPtStatus, ptLoading }) {
    const [src, setSrc] = React.useState('');
    const [dst, setDst] = React.useState('');
    const [clear, setClear] = React.useState(false);

    return (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input value={src} onChange={e => setSrc(e.target.value)} placeholder="Source field (e.g. leaderboardPoints)" className="modern-input" />
            <input value={dst} onChange={e => setDst(e.target.value)} placeholder="Destination field (e.g. tempBackupPoints)" className="modern-input" />
            <label style={{ fontSize: '13px', color: '#ccc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={clear} onChange={e => setClear(e.target.checked)} />
                Zero out source field after copy
            </label>
            <button
                disabled={ptLoading || !src || !dst}
                className="modern-button"
                style={{ background: '#555' }}
                onClick={async () => {
                    if (!window.confirm(`Transfer '${src}' → '${dst}' for ALL players? clearSource=${clear}`)) return;
                    setPtLoading(true);
                    setPtStatus(`Running custom transfer: ${src} → ${dst}…`);
                    try {
                        const r = await callable({ sourceField: src, destinationField: dst, clearSource: clear });
                        setPtStatus(`✅ ${r.data.message}`);
                    } catch (e) {
                        setPtStatus(`❌ Error: ${e.message}`);
                    } finally {
                        setPtLoading(false);
                    }
                }}
            >
                Run Custom Transfer
            </button>
        </div>
    );
}

function AdminPanel({
    adminTargetPlayer, setAdminTargetPlayer,
    adminTargetPlayerStatus,
    sendNftTarget, setSendNftTarget,
    sendAssetId, setSendAssetId,
    sendNftMemo, setSendNftMemo,
    sendNftFeedback,
    isSendingNft,
    handleManualSendNft,
    sendTokenTarget, setSendTokenTarget,
    sendTokenAmount, setSendTokenAmount,
    sendTokenSymbol, setSendTokenSymbol,
    sendTokenMemo, setSendTokenMemo,
    sendTokenFeedback,
    isSendingToken,
    handleManualSendToken,
    masterAccountWaxId,
    adminLoadingStatus,
    checkPlayerStatus,
    handleBlacklist,
    handleUnblacklist,
    addPartnerCallable,
    updatePartnerStatusCallable,
    approvePartnerPoolCallable,
    configureStakeableNftCallable,
    isResettingStamina,
    resetStaminaFeedback,
    airdropDate, setAirdropDate,
    airdropAssetIds, setAirdropAssetIds,
    airdropFeedback,
    isAirdropping,
    handleAirdropFromPast,
    adminResetWeeklyPointsCallable,
    adminRestoreWeeklyPointsCallable,
    adminResetAllWeeklyPointsCallable,
    isResettingDailyPoints,
    resetDailyPointsFeedback,
    handleResetAllDailyPoints,
    adminListStoreItemCallable,
    adminDelistStoreItemCallable,
    manualPayoutSeasonId,
    setManualPayoutSeasonId,
    manualPayoutFeedback,
    setManualPayoutFeedback,
    isManualPayoutProcessing,
    setIsManualPayoutProcessing,
    adminSendWeeklyPayoutCallable,
    adminSendWeeklyDiscordAnnouncementCallable,
    adminConfigureUnboxCallable,
    adminTroubleshootUnboxCallable
}) {
    const [isDiscordPosting, setIsDiscordPosting] = useState(false);
    const [discordFeedback, setDiscordFeedback] = useState('');
    const [stakeConfig, setStakeConfig] = useState([]);
    const [newStakeable, setNewStakeable] = useState({
        templateId: '', templateName: '', collectionName: 'whatifreward',
        boostType: 'MP Boost', boostValue: '', slot: 'Sword', isActive: true
    });

    const [unboxes, setUnboxes] = useState([]);
    const [newUnbox, setNewUnbox] = useState({
        unboxId: '', unboxName: '', outcomes: [], resultCollection: 'whatifreward',
        resultSchema: 'fireman', rollCount: 1, packTemplateId: ''
    });

    const [allPlayers, setAllPlayers] = useState([]);
    const [partners, setPartners] = useState([]);
    const [pendingPools, setPendingPools] = useState([]);
    const [newPartnerId, setNewPartnerId] = useState('');
    const [newPartnerName, setNewPartnerName] = useState('');
    const [feedback, setFeedback] = useState('');
    const [restoreDate, setRestoreDate] = useState('');
    const [restoreFeedback, setRestoreFeedback] = useState('');
    const [isRestoring, setIsRestoring] = useState(false);
    const [isResettingAllWeekly, setIsResettingAllWeekly] = useState(false);
    const [resetAllWeeklyFeedback, setResetAllWeeklyFeedback] = useState('');

    // Level Reset State
    const [isResettingLevels, setIsResettingLevels] = useState(false);
    const [resetLevelsFeedback, setResetLevelsFeedback] = useState('');

    // Points & Stamina Reset State
    const [isResettingPointsAndStamina, setIsResettingPointsAndStamina] = useState(false);
    const [resetPointsAndStaminaFeedback, setResetPointsAndStaminaFeedback] = useState('');
    const [isResettingStaminaOnly, setIsResettingStaminaOnly] = useState(false);
    const [resetStaminaOnlyFeedback, setResetStaminaOnlyFeedback] = useState('');

    // --- Point Manager (self-contained, no props needed) ---
    const [ptLoading, setPtLoading] = useState(false);
    const [ptStatus, setPtStatus] = useState('');
    const adminTransferPointsCallable = httpsCallable(functions, 'adminTransferPoints');
    const adminResetAllPlayersToLevelOneCallable = httpsCallable(functions, 'adminResetAllPlayersToLevelOne');
    const adminResetPointsAndStaminaCallable = httpsCallable(functions, 'adminResetPointsAndStamina');
    const adminResetStaminaOnlyCallable = httpsCallable(functions, 'adminResetStaminaOnly');
    const adminMigratePointsToContractCallable = httpsCallable(functions, 'adminMigratePointsToContract');

    useEffect(() => {
        const configQuery = query(collection(db, "stakeable_nft_templates"), orderBy("collectionName"), orderBy("templateName"));
        const unsubscribeConfig = onSnapshot(configQuery, (snapshot) => {
            setStakeConfig(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const playersQuery = query(collection(db, "players"));
        const unsubscribePlayers = onSnapshot(playersQuery, (snapshot) => {
            setAllPlayers(snapshot.docs.map(d => d.id));
        });

        const partnersQuery = query(collection(db, "partners"));
        const unsubscribePartners = onSnapshot(partnersQuery, (snapshot) => {
            setPartners(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const poolsQuery = query(collection(db, "partner_pools"), where("status", "==", "pending_approval"));
        const unsubscribePools = onSnapshot(poolsQuery, (snapshot) => {
            setPendingPools(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unboxesQuery = query(collection(db, "unbox_configs"), orderBy("createdAt", "desc"));
        const unsubscribeUnboxes = onSnapshot(unboxesQuery, (snapshot) => {
            setUnboxes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubscribeConfig();
            unsubscribePlayers();
            unsubscribePartners();
            unsubscribePools();
            unsubscribeUnboxes();
        };
    }, []);

    const addUnboxOutcome = () => setNewUnbox(prev => ({ ...prev, outcomes: [...prev.outcomes, { templateId: '', name: '', qty: 1, weight: 1, schemaName: '', collectionName: '' }] }));

    const handleUnboxOutcomeChange = (index, field, value) => {
        const updated = [...newUnbox.outcomes];
        updated[index][field] = value;
        setNewUnbox(prev => ({ ...prev, outcomes: updated }));
    };

    const removeUnboxOutcome = (index) => {
        const updated = [...newUnbox.outcomes];
        updated.splice(index, 1);
        setNewUnbox(prev => ({ ...prev, outcomes: updated }));
    };

    const handleSaveUnbox = async () => {
        setFeedback("Saving pack unboxing config...");
        const finalUnboxId = newUnbox.unboxId || `unbox_${Date.now()}`;
        try {
            const calculatedTotalWeight = newUnbox.outcomes.reduce((sum, o) => sum + (parseInt(o.weight) || 0), 0);
            const result = await adminConfigureUnboxCallable({
                unboxId: finalUnboxId,
                unboxName: newUnbox.unboxName,
                packTemplateId: newUnbox.packTemplateId,
                resultCollectionName: newUnbox.resultCollection,
                resultSchemaName: newUnbox.resultSchema,
                outcomes: newUnbox.outcomes,
                totalWeight: calculatedTotalWeight,
                rollCount: parseInt(newUnbox.rollCount) || 1,
                isActive: true
            });
            setFeedback(result.data.message);
            setNewUnbox({
                unboxId: '', unboxName: '', outcomes: [], resultCollection: 'whatifreward',
                resultSchema: 'fireman', rollCount: 1, packTemplateId: ''
            });
        } catch (e) {
            setFeedback(`Error: ${e.message}`);
        }
    };

    const handleToggleUnboxStatus = async (unbox) => {
        try {
            await adminConfigureUnboxCallable({ ...unbox, isActive: !unbox.isActive });
            setFeedback(`Pack Unboxing ${unbox.unboxName} status updated.`);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
    };

    const handleEditUnbox = (unbox) => {
        setNewUnbox({
            unboxId: unbox.id || '',
            unboxName: unbox.unboxName || '',
            packTemplateId: unbox.packTemplateId || '',
            resultCollection: unbox.resultCollectionName || 'whatifreward',
            resultSchema: unbox.resultSchemaName || 'fireman',
            rollCount: unbox.rollCount || 1,
            outcomes: (unbox.outcomes || []).map(o => ({ ...o })),
        });
        setFeedback(`Editing unbox config: ${unbox.unboxName}. Modify and hit Save.`);
        document.getElementById('unbox-form')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleConfigureStakeable = async (configData) => {
        if (!configData.templateId || !configData.boostValue) {
            setFeedback('Error: Template ID and Boost Value are required to save.');
            return;
        }
        try {
            const result = await configureStakeableNftCallable(configData);
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
    };

    const handleAddPartner = async () => {
        if (!newPartnerId || !newPartnerName) {
            setFeedback('Error: Partner WAX ID and Name are required.');
            return;
        }
        try {
            const result = await addPartnerCallable({ partnerId: newPartnerId, partnerName: newPartnerName });
            setFeedback(result.data.message);
            setNewPartnerId('');
            setNewPartnerName('');
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
    };

    const handleTogglePartnerStatus = async (partnerId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const result = await updatePartnerStatusCallable({ partnerId, status: newStatus });
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
    };

    const handleApprovePool = async (poolId) => {
        const boostValue = prompt("This pool is for an Base MP Boost. Enter the percentage boost value per token (e.g., 0.1 for +0.1% HP per token):");
        const boostValueNum = parseFloat(boostValue);
        if (isNaN(boostValueNum) || boostValueNum <= 0) {
            alert("Invalid boost value. Please enter a positive number.");
            return;
        }
        try {
            const result = await approvePartnerPoolCallable({ poolId, boostType: 'Base MP Boost', boostValuePerToken: boostValueNum });
            alert(result.data.message);
        } catch (error) {
            alert(`Error approving pool: ${error.message}`);
        }
    };

    const handleResetWeeklyPoints = useCallback(async () => {
        if (!adminTargetPlayer) return;
        if (!window.confirm(`Are you SURE you want to reset ALL weekly points for ${adminTargetPlayer} to zero?`)) return;
        setFeedback('Resetting weekly points...');
        try {
            const result = await adminResetWeeklyPointsCallable({ targetPlayerId: adminTargetPlayer });
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        }
    }, [adminTargetPlayer, adminResetWeeklyPointsCallable]);

    const handleRestorePoints = useCallback(async () => {
        if (!restoreDate) {
            setRestoreFeedback("Error: A restore date (YYYY-MM-DD) is required.");
            return;
        }
        if (!window.confirm(`This will restore points for ALL players on the ${restoreDate} leaderboard. This is additive and cannot be easily undone. Are you sure?`)) return;
        setIsRestoring(true);
        setRestoreFeedback(`Restoring points for all players from ${restoreDate}...`);
        try {
            const result = await adminRestoreWeeklyPointsCallable({ dateString: restoreDate });
            setRestoreFeedback(result.data.message);
        } catch (error) {
            setRestoreFeedback(`Error: ${error.message}`);
        } finally {
            setIsRestoring(false);
        }
    }, [restoreDate, adminRestoreWeeklyPointsCallable]);

    const handleResetAllWeeklyPoints = useCallback(async () => {
        if (!window.confirm("DANGER: This will reset weekly points for ALL players to zero. This action cannot be undone. Are you absolutely sure?")) return;
        setIsResettingAllWeekly(true);
        setResetAllWeeklyFeedback('Processing global reset... This may take up to a minute.');
        try {
            const result = await adminResetAllWeeklyPointsCallable();
            setResetAllWeeklyFeedback(result.data.message);
        } catch (error) {
            setResetAllWeeklyFeedback(`Error: ${error.message}`);
        } finally {
            setIsResettingAllWeekly(false);
        }
    }, [adminResetAllWeeklyPointsCallable]);

    const handleResetAllLevels = useCallback(async () => {
        if (!window.confirm("DANGER: This will reset EVERY player to Level 1 and 0 XP. This action cannot be undone. Are you absolutely sure?")) return;
        setIsResettingLevels(true);
        setResetLevelsFeedback('Processing global level reset... This may take a minute.');
        try {
            const result = await adminResetAllPlayersToLevelOneCallable();
            setResetLevelsFeedback(result.data.message);
        } catch (error) {
            setResetLevelsFeedback(`Error: ${error.message}`);
        } finally {
            setIsResettingLevels(false);
        }
    }, [adminResetAllPlayersToLevelOneCallable]);

    const handleResetPointsAndStamina = useCallback(async () => {
        if (!window.confirm("DANGER: This will reset Daily/Seasonal points to 0 and restore full stamina for ALL players. Are you absolutely sure?")) return;
        setIsResettingPointsAndStamina(true);
        setResetPointsAndStaminaFeedback('Processing global points & stamina reset... This may take a minute.');
        try {
            const result = await adminResetPointsAndStaminaCallable();
            setResetPointsAndStaminaFeedback(result.data.message);
        } catch (error) {
            setResetPointsAndStaminaFeedback(`Error: ${error.message}`);
        } finally {
            setIsResettingPointsAndStamina(false);
        }
    }, [adminResetPointsAndStaminaCallable]);

    const handleResetStaminaOnly = useCallback(async () => {
        if (!window.confirm("This will safely upgrade ALL players' Stamina and Max Stamina to 100 WITHOUT wiping their daily/weekly points. Are you sure?")) return;
        setIsResettingStaminaOnly(true);
        setResetStaminaOnlyFeedback('Processing global stamina upgrade... This may take a minute.');

        try {
            const result = await adminResetStaminaOnlyCallable();
            setResetStaminaOnlyFeedback(result.data.message);
        } catch (error) {
            setResetStaminaOnlyFeedback(`Error: ${error.message}`);
        } finally {
            setIsResettingStaminaOnly(false);
        }
    }, [adminResetStaminaOnlyCallable]);

    const handleMigratePointsToContract = useCallback(async () => {
        if (!window.confirm("ONE-TIME MIGRATION: Are you sure you want to push all historical leaderboard points into the smart contract? This can take some time.")) return;
        setPtLoading(true);
        setPtStatus("Migrating points to contract...");
        try {
            const result = await adminMigratePointsToContractCallable();
            setPtStatus(result.data.message);
        } catch (error) {
            setPtStatus(`Error: ${error.message}`);
        } finally {
            setPtLoading(false);
        }
    }, [adminMigratePointsToContractCallable]);

    const handleManualWeeklyPayout = useCallback(async () => {
        if (!manualPayoutSeasonId) {
            setManualPayoutFeedback("Error: A Season ID (e.g., season_10) is required.");
            return;
        }
        if (!window.confirm(`DANGER: This will attempt to resend the entire token pool for Season ${manualPayoutSeasonId}. This should ONLY be used if the scheduled reset failed to send tokens. Are you absolutely sure?`)) return;
        setIsManualPayoutProcessing(true);
        setManualPayoutFeedback(`Initiating token payout for ${manualPayoutSeasonId}...`);
        try {
            const result = await adminSendWeeklyPayoutCallable({ seasonId: manualPayoutSeasonId });
            setManualPayoutFeedback(result.data.message);
        } catch (error) {
            console.error("Manual Payout Error:", error);
            setManualPayoutFeedback(`Error: ${error.message}`);
        } finally {
            setIsManualPayoutProcessing(false);
        }
    }, [manualPayoutSeasonId, adminSendWeeklyPayoutCallable, setIsManualPayoutProcessing, setManualPayoutFeedback]);

    const handlePostWeeklyDiscordAnnouncement = useCallback(async () => {
        setIsDiscordPosting(true);
        setDiscordFeedback(manualPayoutSeasonId ? `Sending Discord announcement for ${manualPayoutSeasonId}...` : "Sending Discord announcement for latest season...");
        try {
            const result = await adminSendWeeklyDiscordAnnouncementCallable({ seasonId: manualPayoutSeasonId || undefined });
            setDiscordFeedback(result.data.message || "Discord announcement posted successfully!");
        } catch (error) {
            console.error("Discord Announcement Error:", error);
            setDiscordFeedback(`Error: ${error.message}`);
        } finally {
            setIsDiscordPosting(false);
        }
    }, [manualPayoutSeasonId, adminSendWeeklyDiscordAnnouncementCallable]);

    return (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '800px', margin: 'auto', textAlign: 'left' }}>
            <h2>Admin Panel</h2>
            <p>Master Account: {masterAccountWaxId}</p>
            {feedback && <p style={{ color: feedback.startsWith('Error') ? 'red' : 'green' }}>{feedback}</p>}

            <hr style={{ marginTop: '20px' }} />

            <h3>Global Actions (Resets)</h3>
            <div style={{ border: '1px solid #c83b3b', padding: '15px', borderRadius: '5px', backgroundColor: 'rgba(100,0,0,0.2)' }}>
                <p style={{ color: '#ffaaaa' }}>These actions affect ALL players. Use with extreme caution.</p>

                <div style={{ marginBottom: '15px' }}>
                    <button onClick={handleResetPointsAndStamina} className="modern-button" style={{ backgroundColor: '#ffc107', color: '#000' }} disabled={isResettingPointsAndStamina}>
                        {isResettingPointsAndStamina ? 'Resetting...' : 'MASTER RESET POINTS & FULL HEAL STAMINA'}
                    </button>
                    {resetPointsAndStaminaFeedback && <p style={{ color: resetPointsAndStaminaFeedback.startsWith('Error') ? 'red' : 'green', fontSize: '12px', margin: '5px 0' }}>{resetPointsAndStaminaFeedback}</p>}
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <button
                        onClick={handleResetStaminaOnly}
                        className="modern-button"
                        style={{ backgroundColor: '#00e5ff', color: '#000' }}
                        disabled={isResettingStaminaOnly}
                    >
                        {isResettingStaminaOnly ? 'Upgrading Stamina...' : 'UPGRADE & HEAL STAMINA ONLY (NO POINT LOSS)'}

                    </button>
                    {resetStaminaOnlyFeedback && (
                        <p style={{ color: resetStaminaOnlyFeedback.startsWith('Error') ? 'red' : 'green', fontSize: '12px', margin: '5px 0' }}>
                            {resetStaminaOnlyFeedback}
                        </p>
                    )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <button onClick={handleResetAllDailyPoints} className="modern-button" style={{ backgroundColor: '#fd7e14' }} disabled={isResettingDailyPoints}>
                        {isResettingDailyPoints ? 'Resetting Daily...' : 'MASTER RESET ALL DAILY POINTS'}
                    </button>
                    {resetDailyPointsFeedback && <p style={{ color: resetDailyPointsFeedback.startsWith('Error') ? 'red' : 'green', fontSize: '12px', margin: '5px 0' }}>{resetDailyPointsFeedback}</p>}
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <button onClick={handleResetAllWeeklyPoints} className="modern-button" style={{ backgroundColor: '#b300b3' }} disabled={isResettingAllWeekly}>
                        {isResettingAllWeekly ? 'Resetting Weekly...' : 'MASTER RESET ALL WEEKLY POINTS'}
                    </button>
                    {resetAllWeeklyFeedback && <p style={{ color: resetAllWeeklyFeedback.startsWith('Error') ? 'red' : 'green', fontSize: '12px', margin: '5px 0' }}>{resetAllWeeklyFeedback}</p>}
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <button onClick={handleResetAllLevels} className="modern-button" style={{ backgroundColor: '#c83b3b' }} disabled={isResettingLevels}>
                        {isResettingLevels ? 'Resetting Levels...' : 'MASTER RESET ALL LEVELS TO 1'}
                    </button>
                    {resetLevelsFeedback && <p style={{ color: resetLevelsFeedback.startsWith('Error') ? 'red' : 'green', fontSize: '12px', margin: '5px 0' }}>{resetLevelsFeedback}</p>}
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <button onClick={handleMigratePointsToContract} className="modern-button" style={{ backgroundColor: '#4caf50' }} disabled={ptLoading}>
                        {ptLoading ? 'Migrating...' : 'ONE-TIME MIGRATION: PUSH ALL SCORES TO SMART CONTRACT'}
                    </button>
                    {ptStatus && <p style={{ color: ptStatus.startsWith('Error') ? 'red' : 'green', fontSize: '12px', margin: '5px 0' }}>{ptStatus}</p>}
                </div>
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Manage Pack Unboxing</h3>
            <div id="unbox-form" style={{ border: '1px solid #444', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
                <h4>Create / Update Unboxing Config</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <input value={newUnbox.unboxName} onChange={e => setNewUnbox({ ...newUnbox, unboxName: e.target.value })} placeholder="Unboxing Display Name" className="modern-input" />
                    <input value={newUnbox.unboxId} onChange={e => setNewUnbox({ ...newUnbox, unboxId: e.target.value })} placeholder="Config ID (leave empty for new)" className="modern-input" />
                    <input value={newUnbox.packTemplateId} onChange={e => setNewUnbox({ ...newUnbox, packTemplateId: e.target.value })} placeholder="Pack Template ID to Burn" className="modern-input" />
                    <input value={newUnbox.resultCollection} onChange={e => setNewUnbox({ ...newUnbox, resultCollection: e.target.value })} placeholder="Result Collection Name" className="modern-input" />
                    <input value={newUnbox.resultSchema} onChange={e => setNewUnbox({ ...newUnbox, resultSchema: e.target.value })} placeholder="Default Schema Name (fallback)" className="modern-input" />
                </div>

                <div style={{ marginBottom: '15px', border: '1px solid #555', padding: '10px', borderRadius: '5px' }}>
                    <label style={{ color: 'cyan', display: 'block', marginBottom: '5px' }}>Outcomes Configuration (Weighted Gacha)</label>
                    <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#aaa' }}>
                        Total Weight: <b style={{ color: '#4dff4d' }}>{newUnbox.outcomes.reduce((s, o) => s + (parseInt(o.weight) || 0), 0)}</b>
                    </p>
                    <div style={{ marginBottom: '15px', borderBottom: '1px solid #444', paddingBottom: '10px' }}>
                        <label style={{ display: 'block', color: '#ffaa00', fontSize: '12px' }}>NFTs Rewarded per Pack (Rolls)</label>
                        <input type="number" className="modern-input" style={{ width: '100px', border: '1px solid #ffaa00' }} value={newUnbox.rollCount} onChange={e => setNewUnbox({ ...newUnbox, rollCount: e.target.value })} placeholder="e.g. 5" />
                    </div>

                    {newUnbox.outcomes.map((outcome, idx) => (
                        <div key={idx} style={{ marginBottom: '10px', borderLeft: '3px solid #555', paddingLeft: '8px' }}>
                            <div style={{ display: 'flex', gap: '5px', marginBottom: '4px' }}>
                                <input placeholder="Tpl ID" value={outcome.templateId} onChange={e => handleUnboxOutcomeChange(idx, 'templateId', e.target.value)} className="modern-input" style={{ flex: 2 }} />
                                <input placeholder="Name" value={outcome.name} onChange={e => handleUnboxOutcomeChange(idx, 'name', e.target.value)} className="modern-input" style={{ flex: 3 }} />
                                <input type="number" placeholder="Qty" value={outcome.qty} onChange={e => handleUnboxOutcomeChange(idx, 'qty', parseInt(e.target.value))} className="modern-input" style={{ flex: 1 }} />
                                <input type="number" placeholder="Wgt" value={outcome.weight} onChange={e => handleUnboxOutcomeChange(idx, 'weight', parseInt(e.target.value))} className="modern-input" style={{ flex: 1, border: '1px solid cyan' }} />
                                <button onClick={() => removeUnboxOutcome(idx)} className="modern-button" style={{ background: 'darkred', color: 'white', margin: 0, padding: '5px 10px' }}>X</button>
                            </div>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <input placeholder="Schema (blank=default)" value={outcome.schemaName || ''} onChange={e => handleUnboxOutcomeChange(idx, 'schemaName', e.target.value)} className="modern-input" style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid #666' }} />
                                <input placeholder="Collection (blank=default)" value={outcome.collectionName || ''} onChange={e => handleUnboxOutcomeChange(idx, 'collectionName', e.target.value)} className="modern-input" style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid #666' }} />
                            </div>
                        </div>
                    ))}
                    <button onClick={addUnboxOutcome} className="modern-button" style={{ background: '#28a745', color: 'white', padding: '5px 10px', marginTop: '10px' }}>+ Add Outcome</button>
                </div>
                <button onClick={handleSaveUnbox} className="modern-button">Save Unboxing Config</button>
            </div>

            <h4>Current Unboxing Configs</h4>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #444', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>
                {unboxes.map(unbox => (
                    <div key={unbox.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #333' }}>
                        <div>
                            <span>{unbox.unboxName} (Pack Tpl: {unbox.packTemplateId})</span>
                            <div style={{ fontSize: '12px', color: 'cyan' }}>Outcomes: {unbox.outcomes?.length || 0} items | Rolls: {unbox.rollCount} | Total Weight: {unbox.totalWeight}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <button onClick={() => handleEditUnbox(unbox)} className="modern-button" style={{ margin: 0, padding: '5px 10px', background: '#1565c0' }}>Edit</button>
                            <button onClick={() => handleToggleUnboxStatus(unbox)} className="modern-button" style={{ margin: 0, padding: '5px 10px', background: unbox.isActive ? 'darkred' : 'darkgreen' }}>
                                {unbox.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Manage Partner Access</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                <input type="text" value={newPartnerId} onChange={(e) => setNewPartnerId(e.target.value)} placeholder="Partner WAX ID" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <input type="text" value={newPartnerName} onChange={(e) => setNewPartnerName(e.target.value)} placeholder="Partner Display Name" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <button onClick={handleAddPartner} className="modern-button" style={{ backgroundColor: 'darkblue' }}>Add New Partner</button>
            </div>

            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #444', padding: '10px', borderRadius: '5px' }}>
                {partners.map(p => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #333' }}>
                        <span>{p.partnerName} ({p.id}) - Status: <b style={{ color: p.status === 'active' ? 'lightgreen' : 'orange' }}>{p.status}</b></span>
                        <button onClick={() => handleTogglePartnerStatus(p.id, p.status)} className="modern-button" style={{ backgroundColor: p.status === 'active' ? 'darkred' : 'darkgreen', margin: '0' }}>
                            {p.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                    </div>
                ))}
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Approve Pending Partner Pools</h3>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #444', padding: '10px', borderRadius: '5px' }}>
                {pendingPools.length > 0 ? pendingPools.map(pool => (
                    <div key={pool.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #333' }}>
                        <div>
                            <p style={{ margin: '2px 0' }}><b>Partner:</b> {pool.partnerName} ({pool.partnerId})</p>
                            <p style={{ margin: '2px 0' }}><b>Deposit:</b> {pool.totalDeposit} {pool.tokenSymbol}</p>
                            <p style={{ margin: '2px 0' }}><b>Duration:</b> {pool.durationDays} days</p>
                        </div>
                        <button onClick={() => handleApprovePool(pool.id)} className="modern-button" style={{ backgroundColor: 'darkblue', margin: '0' }}>Approve & Set Boost</button>
                    </div>
                )) : <p>No pools are currently awaiting approval.</p>}
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Configure Stakeable NFTs</h3>
            <div style={{ border: '1px solid #444', padding: '15px', borderRadius: '5px' }}>
                <h4>Add / Update Template</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                    <input type="text" value={newStakeable.templateId} onChange={(e) => setNewStakeable({ ...newStakeable, templateId: e.target.value })} placeholder="Template ID" style={{ padding: '8px' }} />
                    <input type="text" value={newStakeable.templateName} onChange={(e) => setNewStakeable({ ...newStakeable, templateName: e.target.value })} placeholder="Template Name" style={{ padding: '8px' }} />
                    <input type="text" value={newStakeable.collectionName} onChange={(e) => setNewStakeable({ ...newStakeable, collectionName: e.target.value })} placeholder="Collection Name" style={{ padding: '8px' }} />
                    <input type="number" value={newStakeable.boostValue} onChange={(e) => setNewStakeable({ ...newStakeable, boostValue: parseFloat(e.target.value) || '' })} placeholder="Boost Value (%)" style={{ padding: '8px' }} />
                    <select value={newStakeable.boostType} onChange={(e) => setNewStakeable({ ...newStakeable, boostType: e.target.value })} style={{ padding: '8px' }}>
                        <option value="MP Boost">MP Boost</option>
                        <option value="Speed Boost">Speed Boost</option>
                        <option value="Points Gain">Points Gain</option>
                        <option value="XP Gain">XP Gain</option>
                        <option value="Stamina Boost">Stamina Boost</option>
                    </select>
                    <select value={newStakeable.slot} onChange={(e) => setNewStakeable({ ...newStakeable, slot: e.target.value })} style={{ padding: '8px' }}>
                        {['Crest', 'Book', 'Charm', 'Boots', 'Shield', 'Sword'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <button onClick={() => handleConfigureStakeable(newStakeable)} className="modern-button" style={{ width: '100%' }}>Save Configuration</button>
            </div>

            <h4 style={{ marginTop: '20px' }}>Current Configurations</h4>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #444', padding: '10px', borderRadius: '5px' }}>
                {stakeConfig.map(config => (
                    <div key={config.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '5px', marginBottom: '5px', borderRadius: '4px' }}>
                        <div>
                            <p style={{ margin: '0', fontWeight: 'bold' }}>{config.templateName} ({config.id})</p>
                            <p style={{ margin: '2px 0', fontSize: '14px' }}>Collection: {config.collectionName}</p>
                            <p style={{ margin: '2px 0', fontSize: '14px', color: 'cyan' }}>Boosts: +{config.boostValue}% {config.boostType}</p>
                        </div>
                        <button onClick={() => handleConfigureStakeable({ ...config, isActive: !config.isActive })} className="modern-button" style={{ backgroundColor: config.isActive ? 'darkred' : 'darkgreen', margin: 0 }}>
                            {config.isActive ? 'Disable' : 'Enable'}
                        </button>
                    </div>
                ))}
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Manual NFT Distribution</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input type="text" value={sendNftTarget} onChange={(e) => setSendNftTarget(e.target.value)} placeholder="Recipient WAX ID" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <input type="text" value={sendAssetId} onChange={(e) => setSendAssetId(e.target.value)} placeholder="Asset ID to send" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <input type="text" value={sendNftMemo} onChange={(e) => setSendNftMemo(e.target.value)} placeholder="Memo" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <button onClick={handleManualSendNft} className="modern-button" style={{ backgroundColor: 'darkblue' }} disabled={isSendingNft}>
                    {isSendingNft ? 'Sending...' : 'Send NFT'}
                </button>
                {sendNftFeedback && <p style={{ color: sendNftFeedback.startsWith('Error') ? 'red' : 'green' }}>{sendNftFeedback}</p>}
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Manual Token Distribution</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input type="text" value={sendTokenTarget} onChange={(e) => setSendTokenTarget(e.target.value)} placeholder="Recipient WAX ID" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <input type="number" step="any" value={sendTokenAmount} onChange={(e) => setSendTokenAmount(e.target.value)} placeholder="Amount" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <select value={sendTokenSymbol} onChange={(e) => setSendTokenSymbol(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }}>
                    {['WAX', 'WHATIF', 'FIREMAN', 'PUZZL', 'WTFWUF', 'WTFBILL', 'WHATIFX', 'TESTERU', 'XMASZS'].map(token => (
                        <option key={token} value={token}>{token}</option>
                    ))}
                </select>
                <input type="text" value={sendTokenMemo} onChange={(e) => setSendTokenMemo(e.target.value)} placeholder="Memo" style={{ padding: '8px', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <button onClick={handleManualSendToken} className="modern-button" style={{ backgroundColor: 'darkorange' }} disabled={isSendingToken}>
                    {isSendingToken ? 'Sending...' : 'Send Token'}
                </button>
                {sendTokenFeedback && <p style={{ color: sendTokenFeedback.startsWith('Error') ? 'red' : 'green' }}>{sendTokenFeedback}</p>}
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Manage Player Blacklist</h3>
            <div>
                <input id="playerWaxId" type="text" value={adminTargetPlayer} onChange={(e) => setAdminTargetPlayer(e.target.value)} placeholder="Enter player's WAX ID" style={{ width: 'calc(100% - 120px)', padding: '8px', margin: '5px 0', borderRadius: '4px', border: '1px solid #555', backgroundColor: '#333', color: 'white' }} />
                <button onClick={checkPlayerStatus} className="modern-button" disabled={adminLoadingStatus}>
                    {adminLoadingStatus ? 'Checking...' : 'Check Status'}
                </button>
            </div>
            {adminTargetPlayerStatus && <p style={{ marginTop: '10px' }}>Current Status: <b>{adminTargetPlayerStatus}</b></p>}
            <div style={{ marginTop: '20px' }}>
                <button onClick={handleBlacklist} className="modern-button" style={{ backgroundColor: 'darkred' }}>Blacklist Player</button>
                <button onClick={handleUnblacklist} className="modern-button" style={{ backgroundColor: 'darkgreen' }}>Unblacklist Player</button>
                <button onClick={handleResetWeeklyPoints} className="modern-button" style={{ backgroundColor: '#6f42c1' }}>Reset Weekly Points</button>
            </div>

            <hr style={{ marginTop: '20px' }} />

            <h3>Restore Weekly Points from Daily Log</h3>
            <p>This will find a past daily leaderboard and add those points to every player's current weekly score.</p>
            <div>
                <input type="text" value={restoreDate} onChange={(e) => setRestoreDate(e.target.value)} placeholder="Date to Restore From (YYYY-MM-DD)" style={{ width: '100%', padding: '8px', boxSizing: 'border-box', marginBottom: '10px' }} />
                <button onClick={handleRestorePoints} className="modern-button" disabled={isRestoring}>
                    {isRestoring ? 'Restoring...' : 'Restore All Points for Date'}
                </button>
                {restoreFeedback && <p style={{ color: restoreFeedback.startsWith('Error') ? 'red' : 'green' }}>{restoreFeedback}</p>}
            </div>

            <hr style={{ margin: '20px 0', opacity: 0.3 }} />

            <h4>Manual Past Airdrop (NFT)</h4>
            <p><b>Use With Caution:</b> This reruns the reward logic for a past date and sends real NFTs.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input type="text" value={airdropDate} onChange={(e) => setAirdropDate(e.target.value)} placeholder="Target Date (YYYY-MM-DD)" className="modern-input" />
                <textarea value={airdropAssetIds} onChange={(e) => setAirdropAssetIds(e.target.value)} placeholder="Enter Asset IDs (Optional). Leave empty to simulate sending 'Reward Packs'." rows="5" className="modern-input" style={{ fontFamily: 'monospace' }}></textarea>
                <button onClick={handleAirdropFromPast} className="modern-button" style={{ backgroundColor: '#b300b3' }} disabled={isAirdropping}>
                    {isAirdropping ? 'Processing...' : 'Run Past Airdrop'}
                </button>
                {airdropFeedback && <p style={{ color: airdropFeedback.startsWith('Error') ? 'red' : 'green' }}>{airdropFeedback}</p>}
            </div>

            <hr style={{ margin: '20px 0', opacity: 0.3 }} />

            <h4>Manual Weekly Payout (Token RETRY)</h4>
            <p><b>Use ONLY if the scheduled weekly token payout failed.</b> Enter the saved **Season ID** (e.g., `season_10`) to re-send the proportional token rewards.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input type="text" value={manualPayoutSeasonId} onChange={(e) => setManualPayoutSeasonId(e.target.value)} placeholder="Target Season ID (e.g., season_10)" className="modern-input" disabled={isManualPayoutProcessing} />
                <button onClick={handleManualWeeklyPayout} className="modern-button" style={{ backgroundColor: '#cc3300' }} disabled={isManualPayoutProcessing}>
                    {isManualPayoutProcessing ? 'Processing Token Payout...' : 'RETRY Weekly Payout'}
                </button>
                {manualPayoutFeedback && <p style={{ color: manualPayoutFeedback.startsWith('Error') ? 'red' : 'green' }}>{manualPayoutFeedback}</p>}

                <button onClick={handlePostWeeklyDiscordAnnouncement} className="modern-button" style={{ backgroundColor: '#5865F2', marginTop: '5px' }} disabled={isDiscordPosting}>
                    {isDiscordPosting ? 'Posting to Discord...' : '📢 Post Monthly Leaderboard to Discord'}
                </button>
                {discordFeedback && <p style={{ color: discordFeedback.startsWith('Error') ? 'red' : 'green', fontSize: '13px', margin: '5px 0' }}>{discordFeedback}</p>}
            </div>

            <hr style={{ margin: '20px 0', opacity: 0.3 }} />

            <h3 style={{ color: '#00e5ff' }}>🔀 Point Field Manager</h3>
            <p style={{ color: '#aaa', fontSize: '13px', marginBottom: '15px' }}>
                Safely copy or move numeric point values between player fields across ALL player documents.
                Use this for delayed payout prep or leaderboard corrections.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Step 1 */}
                <div style={{ padding: '14px', border: '1px solid #5a3e00', borderRadius: '6px', background: 'rgba(243,156,18,0.07)' }}>
                    <h4 style={{ margin: '0 0 6px 0', color: '#f39c12' }}>Step 1 — Backup Current Points</h4>
                    <p style={{ fontSize: '13px', color: '#ccc', margin: '0 0 10px 0' }}>Copies <code>dailyPoints</code> → <code>tempBackupPoints</code> and zeros <code>dailyPoints</code>. Run before any payout prep.</p>
                    <button
                        disabled={ptLoading}
                        onClick={async () => {
                            if (!window.confirm('Copy dailyPoints → tempBackupPoints and zero dailyPoints for ALL players?')) return;
                            setPtLoading(true); setPtStatus('Running Step 1…');
                            try { const r = await adminTransferPointsCallable({ sourceField: 'dailyPoints', destinationField: 'tempBackupPoints', clearSource: true }); setPtStatus(`✅ ${r.data.message}`); }
                            catch (e) { setPtStatus(`❌ Error: ${e.message}`); }
                            finally { setPtLoading(false); }
                        }}
                        className="modern-button"
                        style={{ background: '#f39c12' }}
                    >
                        Backup Current Points
                    </button>
                </div>

                {/* Step 2 */}
                <div style={{ padding: '14px', border: '1px solid #5a0000', borderRadius: '6px', background: 'rgba(231,76,60,0.07)' }}>
                    <h4 style={{ margin: '0 0 6px 0', color: '#e74c3c' }}>Step 2 — Load Saved Points into Season Pool</h4>
                    <p style={{ fontSize: '13px', color: '#ccc', margin: '0 0 10px 0' }}>Copies <code>leaderboardPoints</code> (last week's rollover) into the active pool so the payout script picks them up. Does NOT clear source.</p>
                    <button
                        disabled={ptLoading}
                        onClick={async () => {
                            if (!window.confirm('Copy leaderboardPoints → leaderboardPoints (noop self-copy to confirm pool) for ALL players? Adjust field names in AdminPanel.js if needed.')) return;
                            setPtLoading(true); setPtStatus('Running Step 2…');
                            try { const r = await adminTransferPointsCallable({ sourceField: 'leaderboardPoints', destinationField: 'leaderboardPoints', clearSource: false }); setPtStatus(`✅ ${r.data.message}`); }
                            catch (e) { setPtStatus(`❌ Error: ${e.message}`); }
                            finally { setPtLoading(false); }
                        }}
                        className="modern-button"
                        style={{ background: '#e74c3c' }}
                    >
                        Load Points into Season Pool
                    </button>
                </div>

                {/* Step 3 */}
                <div style={{ padding: '14px', border: '1px solid #004d1a', borderRadius: '6px', background: 'rgba(46,204,113,0.07)' }}>
                    <h4 style={{ margin: '0 0 6px 0', color: '#2ecc71' }}>Step 3 — Restore Current Week</h4>
                    <p style={{ fontSize: '13px', color: '#ccc', margin: '0 0 10px 0' }}>Copies <code>tempBackupPoints</code> → <code>dailyPoints</code> and zeros <code>tempBackupPoints</code>. Run after the payout is complete.</p>
                    <button
                        disabled={ptLoading}
                        onClick={async () => {
                            if (!window.confirm('Restore tempBackupPoints → dailyPoints and zero tempBackupPoints for ALL players?')) return;
                            setPtLoading(true); setPtStatus('Running Step 3…');
                            try { const r = await adminTransferPointsCallable({ sourceField: 'tempBackupPoints', destinationField: 'dailyPoints', clearSource: true }); setPtStatus(`✅ ${r.data.message}`); }
                            catch (e) { setPtStatus(`❌ Error: ${e.message}`); }
                            finally { setPtLoading(false); }
                        }}
                        className="modern-button"
                        style={{ background: '#2ecc71', color: '#000' }}
                    >
                        Restore Current Board
                    </button>
                </div>

                {/* Custom Transfer */}
                <details style={{ border: '1px solid #333', borderRadius: '6px', padding: '12px' }}>
                    <summary style={{ cursor: 'pointer', color: '#aaa', fontSize: '13px' }}>⚙️ Advanced: Custom Field Transfer</summary>
                    <CustomFieldTransfer callable={adminTransferPointsCallable} setPtLoading={setPtLoading} setPtStatus={setPtStatus} ptLoading={ptLoading} />
                </details>

                {ptLoading && <p style={{ color: '#00e5ff', fontSize: '13px' }}>⏳ Processing — do not close this window...</p>}
                {ptStatus && <div style={{ padding: '10px 14px', background: '#1a2a3a', borderLeft: '4px solid #3498db', borderRadius: '4px', fontSize: '13px' }}>{ptStatus}</div>}
            </div>

            <h3 style={{ marginTop: '30px' }}>All Players (for reference)</h3>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #444', padding: '10px', borderRadius: '5px' }}>
                {allPlayers.length > 0 ? (
                    <ul style={{ listStyleType: 'none', padding: 0 }}>
                        {allPlayers.map(player => (
                            <li key={player}>{player}</li>
                        ))}
                    </ul>
                ) : (
                    <p>Loading players...</p>
                )}
            </div>
        </div>
    );
}

export default AdminPanel;